import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { RATING_MAX, searchExcerpt } from '@mapshare/core';
import {
  activitySummary,
  isPlaceSort,
  PLACE_SORTS,
  sortPlaces,
  type PlaceSort,
} from '../explore/sort.ts';
import { formatDistance } from '../explore/view.ts';
import { LANDING_COPY } from './copy.ts';
import {
  DEMO_ORG,
  DEMO_PLACES,
  DEMO_TAGS,
  demoActivity,
  demoCameraCenter,
  demoDistance,
  demoRecentVisitors,
  filterDemoPlaces,
  type DemoPlace,
  type LatLng,
} from './demo-data.ts';

/**
 * @fileoverview The demo on the landing page: the organization's home as it
 * looks on a phone — search and purpose tags over the map, the list and the
 * place card in a sheet below — with fictional data (OQ-48).
 *
 * **Nothing here talks to the API but the map's style** (`DemoMap`). Search,
 * tags and sorting work on `DEMO_PLACES` in the browser; there is no Places
 * call behind the search box (invariants 3 and 4), and no organization to
 * resolve.
 *
 * **It shows what the real map shows** (2026-09-30): the list's rows carry the
 * rating and visits and sort the four ways the real list does
 * (`explore/sort.ts`, the same functions), a search found by the description
 * says so with an excerpt (`searchExcerpt`), and the card has the ratings,
 * the shared description and members' visits (OQ-52, OQ-57). Writing — rating,
 * recording a visit — is left out: the demo is for reading.
 *
 * **The map waits until the demo is on screen.** The search, the tags and the
 * list work at once; MapLibre (the heaviest chunk MapShare has) is fetched when
 * the phone scrolls into view, so the first paint of the page does not pay
 * for it. Without `IntersectionObserver` it loads straight away.
 */

const DemoMap = lazy(() => import('./DemoMap.tsx'));

const copy = LANDING_COPY.demo;

/** The sheet's share of the screen. The attribution sits on top of it (invariant 5). */
const SHEET_HEIGHT = '44%';

/** Visits the card shows before 「ほか N件」, as the real card does. */
const VISITS_SHOWN = 2;

/** `★★★★☆` for 4. */
function stars(rating: number): string {
  return '★'.repeat(rating) + '☆'.repeat(RATING_MAX - rating);
}

/** `2026-09-24` as `9/24`. */
function shortDay(day: string): string {
  const [, month, date] = day.split('-');
  return `${Number(month)}/${Number(date)}`;
}

function useNearViewport(ref: React.RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const element = ref.current;
    if (near || element === null || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [near, ref]);
  return near;
}

function PlaceCard({ place, onBack }: { place: DemoPlace; onBack: () => void }) {
  const { meters, minutes } = demoDistance(place);
  const { rating } = demoActivity(place);
  const recent = demoRecentVisitors(place);
  const shown = place.visits.slice(0, VISITS_SHOWN);
  return (
    <article className="ms-demo-card" aria-label={place.name} data-testid="demo-card">
      <button type="button" className="ms-demo-back" onClick={onBack}>
        ← {copy.back}
      </button>
      <h3>
        {place.name} <span className="ms-badge ms-badge-fictional">{copy.fictional}</span>
      </h3>
      <p className="ms-demo-muted">{place.address}</p>
      <p className="ms-demo-muted">{copy.distance(formatDistance(meters), minutes)}</p>
      <p className="ms-demo-tags">
        {place.tags.map((tag) => (
          <span key={tag} className="ms-demo-tag">
            {tag}
          </span>
        ))}
      </p>
      <p className="ms-demo-summary" data-testid="demo-summary">
        {rating.average === null ? (
          <span className="ms-demo-muted">{copy.noRating}</span>
        ) : (
          <>
            <span className="ms-demo-stars" aria-label={copy.averageLabel(rating.average)}>
              {stars(Math.round(rating.average))}
            </span>{' '}
            <strong>{rating.average.toFixed(1)}</strong>
            <span className="ms-demo-muted">{copy.raters(rating.count)}</span>
          </>
        )}
        <span className="ms-demo-muted">
          {' ・ '}
          {copy.visitCount(place.visits.length)}
          {recent.length > 0 && ` ・ ${copy.recentVisitors(recent)}`}
        </span>
      </p>
      <h4>{copy.descriptionHeading}</h4>
      <p className="ms-demo-description">{place.description}</p>
      <h4>{copy.visitsHeading}</h4>
      {shown.length === 0 ? (
        <p className="ms-demo-muted">{copy.noVisits}</p>
      ) : (
        <ul className="ms-demo-visits" data-testid="demo-visits">
          {shown.map((visit) => (
            <li key={`${visit.author}-${visit.day}`}>
              <small>
                {shortDay(visit.day)} {visit.author}
              </small>
              {visit.body !== '' && <span>{visit.body}</span>}
            </li>
          ))}
        </ul>
      )}
      {place.visits.length > VISITS_SHOWN && (
        <p className="ms-demo-muted">{copy.moreVisits(place.visits.length - VISITS_SHOWN)}</p>
      )}
      <p className="ms-demo-muted">{copy.addedBy(place.addedBy)}</p>
    </article>
  );
}

export function DemoPhone() {
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [sort, setSort] = useState<PlaceSort>('distance');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [center, setCenter] = useState<LatLng>(() => demoCameraCenter(DEMO_ORG.anchor));
  const phone = useRef<HTMLDivElement | null>(null);
  const showMap = useNearViewport(phone);

  const visible = useMemo(() => filterDemoPlaces(DEMO_PLACES, { query, tag }), [query, tag]);
  // The list in the chosen order. `DEMO_PLACES` is not nearest first, so
  // 「近い順」 is sorted here, where the real list has it from the API.
  const listed = useMemo(() => {
    const rows = visible.map((place) => ({ place, ...demoActivity(place) }));
    const nearest = [...rows].sort(
      (a, b) => demoDistance(a.place).meters - demoDistance(b.place).meters,
    );
    return sortPlaces(nearest, sort);
  }, [visible, sort]);
  // A place filtered away is no longer open.
  const selected = visible.find((place) => place.id === selectedId) ?? null;

  const select = (id: string) => {
    const place = DEMO_PLACES.find((candidate) => candidate.id === id);
    if (place === undefined) return;
    setSelectedId(id);
    setCenter(demoCameraCenter(place));
  };

  return (
    <div className="ms-phone" ref={phone}>
      <div className="ms-phone-screen" data-testid="demo">
        <div className="ms-demo-map">
          {showMap && (
            <Suspense
              fallback={
                <div className="ms-demo-map-placeholder" role="status">
                  {copy.mapLoading}
                </div>
              }
            >
              <DemoMap
                places={visible}
                selectedId={selected?.id ?? null}
                center={center}
                attributionBottom={SHEET_HEIGHT}
                onSelect={select}
              />
            </Suspense>
          )}
        </div>

        <div className="ms-demo-top">
          <input
            type="search"
            className="ms-demo-search"
            aria-label={copy.searchLabel}
            placeholder={copy.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="ms-demo-chips" role="group" aria-label={copy.tagsLabel}>
            {DEMO_TAGS.map((label) => (
              <button
                key={label}
                type="button"
                className="ms-chip"
                aria-pressed={tag === label}
                onClick={() => setTag(tag === label ? null : label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="ms-demo-sheet" style={{ height: SHEET_HEIGHT }}>
          <div className="ms-demo-handle" aria-hidden="true" />
          {selected !== null ? (
            <PlaceCard place={selected} onBack={() => setSelectedId(null)} />
          ) : (
            <section aria-label={copy.listHeading(visible.length)}>
              <div className="ms-demo-list-head">
                <h3 className="ms-demo-list-heading">{copy.listHeading(visible.length)}</h3>
                {visible.length > 1 && (
                  <select
                    className="ms-demo-sort"
                    aria-label={copy.sortLabel}
                    value={sort}
                    onChange={(event) => {
                      if (isPlaceSort(event.target.value)) setSort(event.target.value);
                    }}
                  >
                    {PLACE_SORTS.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {listed.length === 0 ? (
                <p className="ms-demo-muted">{copy.empty}</p>
              ) : (
                <ul className="ms-demo-list">
                  {listed.map((row) => {
                    const { place } = row;
                    const activity = activitySummary(row);
                    const excerpt = searchExcerpt(place, query);
                    return (
                      <li key={place.id}>
                        <button type="button" onClick={() => select(place.id)}>
                          <span className="ms-demo-row-name">{place.name}</span>
                          {activity !== null && (
                            <span className="ms-demo-activity">{activity}</span>
                          )}
                          {excerpt !== null && (
                            <span className="ms-demo-muted" data-testid="demo-excerpt">
                              {copy.excerpt}
                              {excerpt.truncatedStart && '…'}
                              {excerpt.before}
                              <mark>{excerpt.match}</mark>
                              {excerpt.after}
                              {excerpt.truncatedEnd && '…'}
                            </span>
                          )}
                          <span className="ms-demo-muted">
                            {formatDistance(demoDistance(place).meters)} ・ {place.tags.join(' / ')}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
